import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEnvelope, faPhone, faMapMarkerAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // TODO: Implement actual contact form submission
    // For now, just simulate submission
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="gp-page py-6">
      <div className="gp-container max-w-3xl">
        <Link to="/" className="gp-back-link mb-4">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Back to Home
        </Link>

        <div className="gp-page-header">
          <div className="flex items-center gap-2 mb-1">
            <span className="gp-icon-tile">
              <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
            </span>
            <h1 className="gp-page-title">Contact Us</h1>
          </div>
          <p className="gp-page-subtitle">
            Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="gp-card">
            <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--gp-ink)' }}>
              Get in Touch
            </h2>
            <div className="space-y-3.5">
              <div className="flex items-start gap-3">
                <span className="gp-icon-tile !w-8 !h-8 text-xs">
                  <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider gp-text-muted mb-0.5">Email</p>
                  <a href="mailto:support@globalpreps.com" className="text-sm gp-link">
                    support@globalpreps.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="gp-icon-tile !w-8 !h-8 text-xs">
                  <FontAwesomeIcon icon={faPhone} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider gp-text-muted mb-0.5">Phone</p>
                  <a href="tel:+1234567890" className="text-sm gp-link">
                    +1 (234) 567-890
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="gp-icon-tile !w-8 !h-8 text-xs">
                  <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wider gp-text-muted mb-0.5">Address</p>
                  <p className="text-sm" style={{ color: 'var(--gp-ink)' }}>
                    123 Education Street
                    <br />
                    Learning City, LC 12345
                    <br />
                    United States
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--gp-border)' }}>
              <h3 className="text-sm font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                Business Hours
              </h3>
              <p className="text-xs gp-text-muted leading-relaxed">
                Monday - Friday: 9:00 AM - 6:00 PM EST
                <br />
                Saturday: 10:00 AM - 4:00 PM EST
                <br />
                Sunday: Closed
              </p>
            </div>
          </div>

          <div className="gp-card">
            <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--gp-ink)' }}>
              Send us a Message
            </h2>

            {submitted ? (
              <div
                className="p-3 rounded-md text-sm"
                style={{
                  background: 'var(--gp-success-soft)',
                  border: '1px solid var(--gp-success)',
                  color: 'var(--gp-success)',
                }}
                role="status"
              >
                Thank you for your message! We'll get back to you soon.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-xs font-medium mb-1"
                    style={{ color: 'var(--gp-ink)' }}
                  >
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="What is this regarding?"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-medium mb-1"
                    style={{ color: 'var(--gp-ink)' }}
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="input-field resize-none"
                    placeholder="Your message here..."
                  />
                </div>

                <button type="submit" disabled={submitting} className="w-full gp-btn-primary">
                  {submitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
